import {
  ApiAutolinker,
  AutolinkerModel,
  serializeAutolinker,
  UnsavedAutolinkerModel,
} from '../../models/api/autolinkerModel';
import {CompanyModel} from '../../models/api/companyModel';
import {LocalApplicationModel} from '../../models/api/localApplicationModel';
import {deleteAsync, postApiAsync, putApiAsync} from '../../services/http/http';
import {ListMutation} from '../helpers/listMutation';
import {ResourceTableCommonPageType} from '../helpers/resourceListPagination/resourceListPaginationHelpers';

/*
 * Types.
 */

export type AutolinkerListPageType = ResourceTableCommonPageType<AutolinkerModel>;

export enum AutolinkerMutationsEnum {
  CREATE = 'create',
  EDIT = 'edit',
  DELETE = 'delete',
}

interface CreateMutationAction {
  type: AutolinkerMutationsEnum.CREATE;
  payload: {
    application: LocalApplicationModel;
    autolinker: UnsavedAutolinkerModel;
  };
}

interface EditMutationAction {
  type: AutolinkerMutationsEnum.EDIT;
  payload: {
    id: AutolinkerModel['id'];
    url: AutolinkerModel['url'];
    autolinker: UnsavedAutolinkerModel;
  };
}

interface DeleteMutationAction {
  type: AutolinkerMutationsEnum.DELETE;
  payload: {
    company: CompanyModel;
    autolinkerIds: ReadonlyArray<AutolinkerModel['id']>;
  };
}

export type AutolinkerListMutation = CreateMutationAction | EditMutationAction | DeleteMutationAction;

/*
 * Mutations.
 */

const createAutolinkerMutation: ListMutation<AutolinkerListPageType, CreateMutationAction, void> = {
  mutationKey: AutolinkerMutationsEnum.CREATE,

  async runSideEffect(payload) {
    const {application, autolinker} = payload;

    const serializedPayload = serializeAutolinker(autolinker);
    await postApiAsync(ApiAutolinker, `${application.url}/autolinkers`, serializedPayload);
  },

  runOptimisticUpdate(data, payload, pageIndex) {
    // Only update the first page.
    if (pageIndex !== 0) {
      return data;
    }

    // Use dummy `id`, `url` and `createdAt` while waiting for the server to respond.
    const newAutolinker = {
      id: 0,
      url: '',
      createdAt: Date.now(),
      ...payload.autolinker,
    };

    return {
      ...data,
      items: [newAutolinker, ...data.items],
    };
  },
};

const editAutolinkerMutation: ListMutation<AutolinkerListPageType, EditMutationAction, void> = {
  mutationKey: AutolinkerMutationsEnum.EDIT,

  async runSideEffect(payload) {
    const {url, autolinker} = payload;

    const serializedPayload = serializeAutolinker(autolinker);
    await putApiAsync(ApiAutolinker, url, serializedPayload);
  },

  runOptimisticUpdate(data, payload) {
    return {
      ...data,
      items: data.items.map((autolinker) =>
        payload.id === autolinker.id ? {...autolinker, ...payload.autolinker} : autolinker,
      ),
    };
  },
};

const deleteAutolinkerMutation: ListMutation<AutolinkerListPageType, DeleteMutationAction, void> = {
  mutationKey: AutolinkerMutationsEnum.DELETE,

  async runSideEffect(payload) {
    const {company, autolinkerIds} = payload;

    await deleteAsync(`${company.url}/autolinkers_batch`, {
      autolinker_ids: autolinkerIds,
    });
  },

  runOptimisticUpdate(data, payload) {
    return {
      ...data,
      items: data.items.filter((autolinker) => !payload.autolinkerIds.includes(autolinker.id)),
    };
  },
};

export const autolinkerListMutations = [
  createAutolinkerMutation,
  editAutolinkerMutation,
  deleteAutolinkerMutation,
] as const;
